import { Spinner, useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import type React from 'react'
import { useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import * as yup from 'yup'
import DefaultModal from '../../../../../../components/DefaultModal'
import FormInput from '../../../../../../components/FormInput'
import Toast from '../../../../../../components/Toast'
import { useCreateAffiliatePartnerMutation } from '../../../../../../redux/services/coreApi'

interface AddAffiliatePartnerModalProps {
  isOpen: boolean
  onClose: () => void
}

type FormData = {
  name: string
  contactEmail: string
}

const AddAffiliatePartnerModal: React.FC<AddAffiliatePartnerModalProps> = ({
  isOpen,
  onClose,
}) => {
  const schema: yup.SchemaOf<FormData> = yup.object({
    name: yup.string().required('This field is required.'),
    contactEmail: yup
      .string()
      .email('Please enter a valid email')
      .required('This field is required.'),
  })

  const {
    register,
    handleSubmit,
    formState,
    reset: resetForm,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    reValidateMode: 'onChange',
  })

  const toast = useToast()
  const [
    createAffiliatePartner,
    { isLoading: createAffiliatePartnerIsLoading },
  ] = useCreateAffiliatePartnerMutation()

  const onSubmit: SubmitHandler<FormData> = async ({ name, contactEmail }) => {
    try {
      await createAffiliatePartner({
        name,
        contactEmail,
      }).unwrap()

      toast({
        position: 'top-right',
        render: () => {
          return (
            <Toast type="success">
              Affiliate partner created successfully!
            </Toast>
          )
        },
      })
      onClose()
    } catch (error) {
      toast({
        position: 'top-right',
        render: () => {
          return (
            <Toast type="error">
              Failed to create affiliate partner. Please try again.
            </Toast>
          )
        },
      })
    }
  }

  useEffect(() => {
    if (!isOpen) {
      resetForm()
    }
  }, [isOpen, resetForm])

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={false}
      title="Add New Affiliate Partner"
      whiteBG
    >
      <div className="p-5 pt-0 space-y-5">
        <FormInput
          inputMode="text"
          registeredForm={register('name')}
          error={formState.errors?.name}
          label="Partner Name"
          placeholder="Enter partner name"
        />
        <FormInput
          inputMode="email"
          registeredForm={register('contactEmail')}
          error={formState.errors?.contactEmail}
          label="Contact Email"
          placeholder="Enter contact email"
        />
        <button
          className="button w-full"
          disabled={!formState.isValid || createAffiliatePartnerIsLoading}
          onClick={handleSubmit(onSubmit)}
        >
          {createAffiliatePartnerIsLoading ? (
            <Spinner size="sm" />
          ) : (
            'Create Affiliate Partner'
          )}
        </button>
      </div>
    </DefaultModal>
  )
}

export default AddAffiliatePartnerModal
